import { apiHeaders, ApiError } from "../api";

/** Mirror of the server's AssistantEvent (apps/api/src/assistant/chat.ts). */
export type AssistantEvent =
  | { type: "text"; delta: string }
  | { type: "tool"; id: string; name: string; label: string; status: "start" }
  | { type: "tool"; id: string; name: string; label: string; status: "done"; operationId?: string; executionMode?: string; recordUrl?: string; mutates: boolean; error?: string }
  | { type: "pending_text"; id: string; customerId: string; customerName: string; channel: "sms" | "email"; body: string; subject?: string; policyId?: string; consent: boolean }
  | { type: "pending_action"; id: string; action: string; label: string; input: Record<string, unknown> }
  | { type: "file"; id: string; documentId: string; title: string; url: string }
  | { type: "memory"; id: string; text: string; removed?: boolean }
  | { type: "navigate"; path: string; note?: string }
  | { type: "done"; conversationId: string; title: string; usage: { input: number; output: number; cached: number } }
  | { type: "error"; message: string };

/** A file the person attached: base64 without the data: prefix. Images are resized in the browser before this point. */
export interface Attachment { name: string; mediaType: string; data: string; preview?: string }

export type AssistantMode = "ask" | "act";
export interface ConversationSummary { id: string; title: string; createdAt: string; updatedAt: string; messageCount: number; preview: string }
export interface StoredTurn { at: string; user: { text: string; attachments: Array<{ name: string; mediaType: string }> }; events: AssistantEvent[] }
export interface StoredConversation { id: string; title: string; createdAt: string; updatedAt: string; messageCount: number; turns: StoredTurn[] }

/**
 * One turn with the assistant, streamed. Calls `onEvent` for each server-sent event; resolves when the stream ends.
 * The `done` event names the conversation the server saved; send its id with the next message.
 */
export async function streamAssistant(
  body: { conversationId?: string; message: string; page: string; locale: "en" | "es"; attachments?: Array<Omit<Attachment, "preview">>; notes?: string[] },
  onEvent: (ev: AssistantEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch("/api/assistant/chat", { method: "POST", headers: await apiHeaders("faraday"), body: JSON.stringify(body), signal });
  if (res.status === 401) throw new ApiError("Sign in required", 401);
  if (!res.ok || !res.body) {
    let msg = `Assistant request failed (${res.status})`;
    try { msg = ((await res.json()) as { error?: string }).error ?? msg; } catch { /* keep the status message */ }
    throw new ApiError(msg, res.status);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data: ")) continue;
        try { onEvent(JSON.parse(line.slice(6)) as AssistantEvent); } catch { /* ignore a malformed frame */ }
      }
    }
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { ...init, headers: { ...(await apiHeaders("faraday")), ...(init.headers ?? {}) } });
  if (res.status === 401) throw new ApiError("Sign in required", 401);
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(body.error ?? `Request failed (${res.status})`, res.status);
  return body;
}

export const listConversations = () => call<{ conversations: ConversationSummary[]; prefs: { mode: AssistantMode } }>("/api/assistant/conversations");
export const getConversation = (id: string) => call<{ conversation: StoredConversation }>(`/api/assistant/conversations/${encodeURIComponent(id)}`);
export const renameConversation = (id: string, title: string) => call<{ conversation: ConversationSummary }>(`/api/assistant/conversations/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ title }) });
export const deleteConversation = (id: string) => call<{ deleted: boolean }>(`/api/assistant/conversations/${encodeURIComponent(id)}`, { method: "DELETE" });
export const setMode = (mode: AssistantMode) => call<{ prefs: { mode: AssistantMode } }>("/api/assistant/prefs", { method: "PUT", body: JSON.stringify({ mode }) });

/** Tell the server a conversation was left so it can keep a short memory of it. Fire and forget. */
export async function closeAssistant(conversationId: string): Promise<void> {
  // No `keepalive`: browsers cap keepalive bodies at 64 KB and the server loads the thread itself anyway.
  try { await fetch("/api/assistant/close", { method: "POST", headers: await apiHeaders("faraday"), body: JSON.stringify({ conversationId }) }); } catch { /* best effort */ }
}

const IMAGE_MAX_EDGE = 1600;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf", "text/csv", "text/plain", "application/json"];

/** Read a file the person picked: photos are resized to a sensible edge and re-encoded as JPEG so a phone shot is a few hundred KB. */
export async function prepareAttachment(file: File): Promise<Attachment> {
  let mediaType = file.type || (file.name.toLowerCase().endsWith(".csv") ? "text/csv" : "application/octet-stream");
  if (mediaType === "image/heic" || mediaType === "image/heif" || (mediaType.startsWith("image/") && !ACCEPTED.includes(mediaType))) mediaType = "image/jpeg";
  if (!ACCEPTED.includes(mediaType)) throw new Error(`${file.name}: photos, PDF, CSV or text only`);
  if (mediaType.startsWith("image/") && mediaType !== "image/gif") {
    const resized = await resizeImage(file);
    if (resized) return { name: file.name, mediaType: "image/jpeg", data: resized.split(",")[1]!, preview: resized };
  }
  const dataUrl = await readAsDataUrl(file);
  return { name: file.name, mediaType, data: dataUrl.split(",")[1]!, preview: mediaType.startsWith("image/") ? dataUrl : undefined };
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result)); r.onerror = () => reject(r.error); r.readAsDataURL(file); });
}

async function resizeImage(file: File): Promise<string | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    return canvas.toDataURL("image/jpeg", 0.85);
  } catch {
    return null;
  }
}
